// ==========================
// Version 1 — pages/AdminActivity.js
// - Read-only audit log of /admin_activity
// - Cursor-based paging (limit/startAfter on createdAt desc)
// - Lightweight filter (client-side search on current page)
// - Row inspector (expand details JSON)
// - Friendly error banner (no crash)
// ==========================
import React from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  orderBy,
  query,
  limit,
  startAfter,
} from 'firebase/firestore';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';

const PAGE_SIZE = 30;

export default function AdminActivity() {
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState('');
  const [qtxt, setQtxt] = React.useState('');

  // paging
  const [page, setPage] = React.useState(0);
  const [hasNext, setHasNext] = React.useState(false);
  const cursorsRef = React.useRef([]); // last doc snapshot per page

  const loadPage = React.useCallback(async (pageIndex) => {
    setLoading(true);
    setError('');
    try {
      const col = collection(db, 'admin_activity');
      const base = query(col, orderBy('createdAt', 'desc'), limit(PAGE_SIZE + 1));
      const cursor = pageIndex > 0 ? cursorsRef.current[pageIndex - 1] : null;
      const qy = cursor
        ? query(col, orderBy('createdAt', 'desc'), startAfter(cursor), limit(PAGE_SIZE + 1))
        : base;

      const snap = await getDocs(qy);
      const docs = snap.docs;
      const pageRows = docs.slice(0, PAGE_SIZE).map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          action: data.action || '',
          actorUid: data.actorUid || '',
          targetUid: data.targetUid || '',
          details: data.details || null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
        };
      });

      // set cursor for this page
      const lastForThisPage = docs.length > 0 ? docs[Math.min(docs.length - 1, PAGE_SIZE - 1)] : null;
      if (lastForThisPage) {
        const next = [...cursorsRef.current];
        next[pageIndex] = lastForThisPage;
        cursorsRef.current = next;
      }

      setRows(pageRows);
      setHasNext(docs.length > PAGE_SIZE);
      setPage(pageIndex);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[AdminActivity] load failed:', e);
      setError(
        e?.message?.includes('index') || e?.message?.includes('index') ? (
          'Could not load activity (index may still be building). Try again shortly.'
        ) : (
          'Could not load activity.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  const goNext = () => { if (hasNext) loadPage(page + 1); };
  const goPrev = () => { if (page > 0) loadPage(page - 1); };
  const goFirst = () => loadPage(0);
  const refresh = () => loadPage(page);

  // client-side filter on current page
  const filtered = rows.filter((r) => {
    const t = qtxt.trim().toLowerCase();
    if (!t) return true;
    const hay = [
      r.id,
      r.action,
      r.actorUid,
      r.targetUid,
      r.createdAt ? r.createdAt.toISOString() : '',
      JSON.stringify(r.details || {}),
    ].join(' ').toLowerCase();
    return hay.includes(t);
  });

  return (
    <div>
      <h1 style={h1}>Admin Activity</h1>
      {error ? <div style={err}>{error}</div> : null}

      <div style={toolbar}>
        <input
          placeholder="Search action, actor, target, details… (current page)"
          value={qtxt}
          onChange={(e) => setQtxt(e.target.value)}
          style={input}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={goFirst} style={btnMinor} disabled={loading || page === 0}>First</button>
          <button type="button" onClick={goPrev} style={btnMinor} disabled={loading || page === 0}>Prev</button>
          <button type="button" onClick={goNext} style={btnMinor} disabled={loading || !hasNext}>Next</button>
          <button type="button" onClick={refresh} style={btnMinor} disabled={loading}>Refresh</button>
        </div>
      </div>

      <div style={{ color: WARM_GRAY, fontSize: 12, margin: '4px 0 10px' }}>
        Page {page + 1} • Showing {filtered.length}/{rows.length}
      </div>

      <div style={table}>
        <div style={thead}>
          <div>When</div>
          <div>Action</div>
          <div>Actor</div>
          <div>Target</div>
          <div>Details</div>
        </div>

        {loading ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>No activity on this page.</div>
        ) : (
          filtered.map((r) => <Row key={r.id} row={r} />)
        )}
      </div>
    </div>
  );
}

function Row({ row }) {
  const [open, setOpen] = React.useState(false);
  const when =
    row.createdAt
      ? new Intl.DateTimeFormat(undefined, {
          year: 'numeric', month: 'short', day: '2-digit',
          hour: '2-digit', minute: '2-digit'
        }).format(row.createdAt)
      : '—';

  return (
    <div style={trow}>
      <div title={row.createdAt?.toISOString?.() || ''}>{when}</div>
      <div><Badge>{row.action || '—'}</Badge></div>
      <code style={code}>{row.actorUid || '—'}</code>
      <code style={code}>{row.targetUid || '—'}</code>
      <div>
        {row.details ? (
          <>
            <button type="button" style={btnTiny} onClick={() => setOpen((v) => !v)}>
              {open ? 'Hide' : 'View'}
            </button>
            {open && (
              <pre style={pre}>{JSON.stringify(row.details, null, 2)}</pre>
            )}
          </>
        ) : (
          <span style={{ color: WARM_GRAY }}>—</span>
        )}
      </div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      border: `1px solid ${GOLD}`,
      background: '#fff',
      color: NAVY,
      fontSize: 12,
      fontWeight: 800,
    }}>
      {children}
    </span>
  );
}

/* --- styles --- */
const h1 = { margin: '0 0 12px', fontFamily: 'serif', fontSize: 26, color: IVORY };
const err = { color: BURGUNDY, background: 'rgba(92,26,43,0.08)', border: '1px dashed rgba(183,110,121,0.55)', padding: 10, borderRadius: 10, margin: '8px 0' };

const toolbar = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 };
const input = {
  flex: 1,
  padding: 10,
  borderRadius: 10,
  border: '1px solid rgba(253,252,249,0.2)',
  background: '#fff',
};

const table = { display: 'grid', gap: 6 };
const thead = {
  display: 'grid',
  gridTemplateColumns: '1.2fr 1fr 1.2fr 1.2fr 1.2fr',
  gap: 8,
  fontWeight: 800,
  color: IVORY,
  background: 'rgba(253,252,249,0.06)',
  padding: 10,
  borderRadius: 10,
};

const trow = {
  display: 'grid',
  gridTemplateColumns: '1.2fr 1fr 1.2fr 1.2fr 1.2fr',
  gap: 8,
  background: '#fff',
  color: NAVY,
  padding: 10,
  borderRadius: 10,
  alignItems: 'start',
};

const code = { fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const btnMinor = {
  appearance: 'none',
  background: 'transparent',
  color: IVORY,
  border: `1px solid ${GOLD}`,
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 700,
};

const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};

const pre = {
  margin: '6px 0 0',
  padding: 10,
  borderRadius: 10,
  background: '#fff',
  border: '1px solid rgba(10,26,51,0.12)',
  overflow: 'auto',
  maxHeight: 220,
};
// ==========================
// End Version 1 — pages/AdminActivity.js
// ==========================
